import { CommonModule } from '@angular/common';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Input,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CompanyContactResponse } from '@interfaces/companys/company-contact.interface';
import { TranslateService } from '@ngx-translate/core';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { CompanyContactHttpService } from '@services/http/companys/company-contact-http.service';
import { VaultBaseComponent } from '@src/app/shared/components/base/vault-base.component';
import { SharedModule } from '@src/app/shared/shared.module';
import { combineLatest, Observable } from 'rxjs';
import { finalize, first, map } from 'rxjs/operators';
import { Permissions } from '@core/guard/Permissions';
import { PermissionsService } from '@services/helpers/permissions.service';
import {
  CompanyContactModalComponent,
  ContactModalResult,
} from './company-contact-modal.component';
import { riskReportNotice, sortContacts, typeChips } from './company-contacts-rules';

/**
 * Lista de contatos de uma empresa, com cadastro, edição e exclusão.
 *
 * **Um componente, dois lugares:** a seção "Contatos" do cadastro de empresa e a modal aberta pelo
 * menu de elipses da listagem. Duplicar a tabela nos dois garantiria que um dia divergissem — e
 * uma coluna presente num lugar e ausente no outro é o tipo de diferença que ninguém percebe até
 * alguém reclamar que "some contato".
 */
@Component({
  selector: 'app-company-contacts-panel',
  standalone: true,
  imports: [CommonModule, FormsModule, SharedModule, CompanyContactModalComponent],
  templateUrl: './company-contacts-panel.component.html',
  styleUrls: ['./company-contacts-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompanyContactsPanelComponent extends VaultBaseComponent {
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild('contactModal') contactModal: CompanyContactModalComponent | null = null;

  private readonly companyIdSignal = signal<string | null>(null);

  /**
   * Empresa dona dos contatos. Nula enquanto a empresa ainda não foi salva — o CRUD é por id, e
   * nesse caso a tela explica em vez de mostrar uma tabela vazia que não aceita cadastro.
   */
  @Input() set companyId(value: string | null | undefined) {
    const normalized = value?.trim() || null;

    if (normalized === this.companyIdSignal()) {
      return;
    }

    this.companyIdSignal.set(normalized);
    this.contacts.set([]);
    this.loadError.set(null);

    if (normalized) {
      this.load();
    }
  }

  readonly contacts = signal<CompanyContactResponse[]>([]);
  readonly isLoadingContacts = signal(false);
  readonly isSaving = signal(false);
  readonly loadError = signal<string | null>(null);

  readonly hasCompany = computed(() => this.companyIdSignal() !== null);
  readonly notice = computed(() => riskReportNotice(this.contacts()));
  readonly chipsOf = typeChips;

  // Cada ação aparece com a permissão que o backend confere: a do cadastro de empresas (tenant) ou a própria dos
  // contatos (COMPANYCONTACT_MANAGE, que é como o usuário da empresa cuida dos contatos dela).
  private readonly permissions = inject(PermissionsService);
  readonly canCreate = this.allowedBy(Permissions.Company.CREATE);
  readonly canUpdate = this.allowedBy(Permissions.Company.UPDATE);
  readonly canDelete = this.allowedBy(Permissions.Company.DELETE);
  readonly hasRowActions = computed(() => this.canUpdate() || this.canDelete());

  constructor(
    translateService: TranslateService,
    private readonly contactHttpService: CompanyContactHttpService,
    private readonly toast: ToastHelperService
  ) {
    super(translateService);
  }

  /** Sinal verdadeiro com a permissão do cadastro de empresas OU com a permissão própria de contatos. */
  private allowedBy(companyPermission: string) {
    const allowed$: Observable<boolean> = combineLatest([
      this.permissions.hasPermission(companyPermission),
      this.permissions.hasPermission(Permissions.CompanyContact.MANAGE),
    ]).pipe(map(([byCompany, byContact]) => byCompany || byContact));

    return toSignal(allowed$, { initialValue: false });
  }

  load(): void {
    const companyId = this.companyIdSignal();

    if (!companyId) {
      return;
    }

    this.isLoadingContacts.set(true);
    this.loadError.set(null);

    this.contactHttpService
      .list(companyId)
      .pipe(
        first(),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isLoadingContacts.set(false))
      )
      .subscribe({
        next: data => this.contacts.set(sortContacts(data ?? [])),
        error: () => this.loadError.set('Não foi possível carregar os contatos desta empresa.'),
      });
  }

  openNew(): void {
    this.contactModal?.open(null);
  }

  openEdit(contact: CompanyContactResponse): void {
    this.contactModal?.open(contact);
  }

  onConfirmed(result: ContactModalResult): void {
    const companyId = this.companyIdSignal();

    if (!companyId) {
      return;
    }

    this.isSaving.set(true);

    const request$ = result.contactId
      ? this.contactHttpService.update(companyId, result.contactId, result.payload)
      : this.contactHttpService.create(companyId, result.payload);

    request$
      .pipe(
        first(),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isSaving.set(false))
      )
      .subscribe({
        // Recarrega em vez de mexer na lista local: o backend normaliza o e-mail e pode recusar a
        // gravação por unicidade, e a tela não pode mostrar um contato que não foi salvo.
        next: () => {
          this.toast.showToastSuccess(
            result.contactId ? 'Contato atualizado.' : 'Contato cadastrado.'
          );
          this.load();
        },
        error: error =>
          this.toast.showToastError(
            error?.errors?.[0]?.message ??
              'Não conseguimos salvar o contato. Confira o nome e o e-mail e tente de novo.'
          ),
      });
  }

  remove(contact: CompanyContactResponse): void {
    const companyId = this.companyIdSignal();

    if (!companyId || !confirm(`Excluir o contato ${contact.name}?`)) {
      return;
    }

    this.isSaving.set(true);

    this.contactHttpService
      .delete(companyId, contact.id)
      .pipe(
        first(),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isSaving.set(false))
      )
      .subscribe({
        next: () => {
          this.toast.showToastSuccess('Contato excluído.');
          this.load();
        },
        error: error =>
          this.toast.showToastError(
            error?.errors?.[0]?.message ?? 'Não conseguimos excluir o contato. Tente de novo.'
          ),
      });
  }
}
